<template>
  <article
    class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300"
  >
    <!-- Featured Image -->
    <figure v-if="getTripImageUrl(trip)" class="aspect-video">
      <img
        :src="getTripImageUrl(trip)"
        :alt="getTripImageAlt(trip)"
        class="w-full h-full object-cover"
      />
    </figure>

    <div class="card-body flex-1 flex flex-col">
      <!-- Destination -->
      <div class="flex flex-wrap gap-1 mb-2" v-if="getTripDestination(trip)">
        <div class="badge badge-outline">
          {{ getTripDestination(trip) }}
        </div>
      </div>

      <!-- Title -->
      <h2 class="card-title text-lg mb-2 line-clamp-2">
        {{ getTripTitle(trip) }}
      </h2>

      <!-- Description -->
      <p class="text-base-content/70 text-sm mb-4 line-clamp-3">
        {{ getTripDescription(trip) }}
      </p>

      <!-- Meta Information -->
      <div
        class="flex items-center justify-between text-sm text-base-content/60 mb-4"
      >
        <span v-if="getTripDuration(trip)">
          {{ getTripDuration(trip) }} días
        </span>

        <span class="font-semibold text-primary">
          {{ formatPrice(displayPrice) }}
        </span>
      </div>

      <!-- Action Button -->
      <div class="card-actions justify-end mt-auto">
        <NuxtLink :to="`/trips/${trip.id}`" class="btn btn-primary">
          Ver viaje
        </NuxtLink>
      </div>
    </div>
  </article>
</template>

<script setup>
const props = defineProps({
  trip: {
    type: Object,
    required: true,
  },
});

// Use static pricing for landing page to prevent hydration issues
const displayPrice = computed(() => {
  return getTripPrice(props.trip);
});

// Helper functions for trip data
const getTripImageUrl = (trip) => {
  if (trip?.featured_image) {
    return `/api/images/${trip.featured_image}`;
  }
  // Fallback image
  return "/logo.png";
};

const getTripImageAlt = (trip) => {
  return getTripTitle(trip) || "Imagen del viaje";
};

const getTripTitle = (trip) => {
  return trip?.name || "Sin título";
};

const getTripDescription = (trip) => {
  return trip?.description || "Sin descripción";
};

const getTripDestination = (trip) => {
  // Try to get destination name from the populated destination_start
  if (trip?.destination_start?.name) {
    return trip.destination_start.name;
  }
  return null;
};

const getTripDuration = (trip) => {
  // Calculate total duration from segments
  if (trip?.segments && Array.isArray(trip.segments)) {
    return trip.segments.reduce((total, segment) => {
      return total + (segment?.duration || 1);
    }, 0);
  }
  // Fallback: calculate from start/end dates if available
  if (
    trip?.start_day &&
    trip?.end_day &&
    trip?.start_month &&
    trip?.end_month
  ) {
    // Simple approximation - you might want to make this more accurate
    return Math.max(1, trip.end_day - trip.start_day + 1);
  }
  return 1;
};

const getTripPrice = (trip) => {
  return trip?.price || 0;
};

const formatPrice = (price) => {
  if (!price) return "Consultar precio";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(price);
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
